import {
  useQuery,
  useInfiniteQuery,
  useMutation,
  useQueryClient,
  keepPreviousData,
} from "@tanstack/react-query";
import {
  addContactManuallyAPI,
  bulkDeleteContactsAPI,
  deleteContactAPI,
  fetchContactsAPI,
  fetchContactDetailAPI,
  generateMatchScoreAPI,
  generateMatchScoreForSingleContactAPI,
  uploadContactsCSV,
  updateContactAPI,
  updateContactConsentAPI,
  bulkUpdateContactConsentAPI,
  fetchContactGroupsAPI
} from "../api/contactsApi";
import { useInfiniteScroll } from "@/shared/hooks/useInfiniteScroll";
import {
  showSuccess,
  showError,
  showWarning,
  showErrorFromMutation,
} from "@/shared/utils/toast";

export const useInfiniteContacts = (
  limit = 10,
  searchQuery = "",
  appliedFilters = {},
  enabled = true,
) => {
  const infiniteQuery = useInfiniteQuery({
    queryKey: ["contacts-infinite", limit, searchQuery, appliedFilters],
    queryFn: ({ pageParam }) =>
      fetchContactsAPI(pageParam, limit, searchQuery, appliedFilters),
    initialPageParam: 1,
    getNextPageParam: (lastPage, allPages) => {
      const total = lastPage?.total ?? 0;
      const loadedCount = allPages.reduce(
        (sum, p) => sum + (p?.contacts?.length ?? 0),
        0,
      );
      return loadedCount < total
        ? (lastPage?.page ?? allPages.length) + 1
        : undefined;
    },
    enabled,
    staleTime: 5 * 60 * 1000,
    retry: 2,
  });
  return useInfiniteScroll(infiniteQuery, (page) => page?.contacts ?? []);
};

export const useContacts = (
  page = 1,
  limit = 10,
  companyFilter = "",
  searchQuery = "",
  appliedFilters = {},
  enabled = true,
) => {
  const { data, isLoading, isFetching, isError, error, refetch } = useQuery({
    queryKey: [
      "contacts",
      page,
      limit,
      companyFilter,
      searchQuery,
      appliedFilters,
    ],
    queryFn: () => fetchContactsAPI(page, limit, searchQuery, appliedFilters),
    enabled,
    staleTime: 5 * 60 * 1000,
    retry: 2,
    placeholderData: keepPreviousData,
  });

  // Extract contacts and pagination info from response
  const contacts = data?.contacts || [];
  const totalCount = data?.total || null;
  const currentPage = data?.page || page;
  const currentLimit = data?.limit || limit;
  return {
    contacts,
    totalCount, // Total count from API
    currentPage,
    currentLimit,
    isLoading,
    isFetching,
    isError,
    error,
    refetch,
  };
};

// upload csv (optional columnMapping from field-mapping step)
export const useUploadCSV = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ file, columnMapping, skipMissing }) =>
      uploadContactsCSV(file, columnMapping ?? null, { skipMissing }),
    onSuccess: async (data) => {
      await queryClient.invalidateQueries({
        queryKey: ["contacts"],
        refetchType: "all",
      });
      queryClient.invalidateQueries({ queryKey: ["contacts-infinite"] });
      await queryClient.invalidateQueries({ queryKey: ["contact-groups"] });
      const count = data?.count ?? data?.data?.count ?? 0;
      const msg = data?.message ?? data?.data?.message;
      const skipped = data?.skipped ?? data?.data?.skipped ?? [];
      if (count > 0) {
        showSuccess(
          `Successfully imported ${count} contact${count !== 1 ? "s" : ""}!`,
        );
      }
      if (skipped.length > 0) {
        // Reasons with counts only; the rows themselves were listed in the import dialog.
        const byReason = new Map();
        skipped.forEach((item) => {
          const reason = item.reason || "Failed to import";
          byReason.set(reason, (byReason.get(reason) || 0) + 1);
        });
        showWarning(
          `${skipped.length} contact${skipped.length !== 1 ? "s were" : " was"} skipped`,
          [
            ...[...byReason].slice(0, 5).map(([reason, n]) => `• ${reason} (${n})`),
            ...(byReason.size > 5 ? [`• ${byReason.size - 5} other reasons`] : []),
          ].join("\n"),
        );
      } else if (count === 0) {
        showSuccess(msg || "Contacts imported successfully");
      }
    },
    onError: (error) => {
      showErrorFromMutation(
        error,
        error.message ||
          error.response?.data?.error ||
          error.response?.data?.message ||
          "Failed to upload contacts. Please refresh the page.",
      );
    },
  });
};

// delete contact hook
export const useDeleteContact = (onSuccess) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: deleteContactAPI,
    onSuccess: async () => {
      await queryClient.invalidateQueries({
        queryKey: ["contacts"],
        refetchType: "all",
      });
      queryClient.invalidateQueries({ queryKey: ["contacts-infinite"] });
      await queryClient.invalidateQueries({ queryKey: ["contact-groups"] });
      onSuccess?.();
      showSuccess("Contact deleted successfully");
    },
  });
};

// bulk delete contacts hook
export const useBulkDeleteContacts = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ contactIds }) => bulkDeleteContactsAPI({ contactIds }),
    onSuccess: async (data) => {
      await queryClient.invalidateQueries({ queryKey: ["contacts"] });
      queryClient.invalidateQueries({ queryKey: ["contacts-infinite"] });
      await queryClient.invalidateQueries({ queryKey: ["contact-groups"] });
      if (data.data?.deletedCount > 0 && data.data?.skipped?.length === 0) {
        showSuccess(`Deleted ${data.data?.deletedCount} contacts successfully`);
      } else if (
        data.data?.skipped?.length > 0 &&
        data.data?.deletedIds?.length > 0
      ) {
        showWarning(
        `Deleted ${data.data?.deletedIds?.length} contacts successfully!`,
        `Skipped ${data.data?.skipped?.length} contact(s). Enrolled in campaign!`,
        );
      } else if (data.data?.skipped?.length > 0 && data.data?.deletedIds?.length === 0) {
        showError(
          `Failed to delete contacts!`,
          `Skipped ${data.data?.skipped?.length} contact(s). Enrolled in campaign!`,
        );
      }
    },
    onError: (error) => {
      showErrorFromMutation(
        error,
        error.response?.data?.message ||
          "Failed to delete contacts. Please refresh the page.",
      );
    },
  });
};

// add contact manually hook
export const useAddContactManually = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (contactData) => addContactManuallyAPI(contactData),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ["contacts"] });
      queryClient.invalidateQueries({ queryKey: ["contacts-infinite"] });
      await queryClient.invalidateQueries({ queryKey: ["contact-groups"] });
      showSuccess("Contact added successfully");
    },
  });
};

// generate match score hook
export const useGenerateMatchScore = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ userId, contactIds }) => generateMatchScoreAPI({ userId, contactIds }),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ["contacts"] });
      queryClient.invalidateQueries({ queryKey: ["contacts-infinite"] });
      await queryClient.invalidateQueries({ queryKey: ["contact-groups"] });
    },
  });
};


// generate match score for a single contact hook
export const useGenerateMatchScoreForSingleContact = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ userId, contactId }) => generateMatchScoreForSingleContactAPI({ userId, contactId }),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ["contacts"] });
      queryClient.invalidateQueries({ queryKey: ["contacts-infinite"] });
      await queryClient.invalidateQueries({ queryKey: ["contact-groups"] });
    },
  });
};

// update contact hook
export const useUpdateContact = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ contactId, contactData }) => updateContactAPI({ contactId, contactData }),
    onSuccess: async (_data, variables) => {
      await queryClient.invalidateQueries({ queryKey: ["contacts"] });
      queryClient.invalidateQueries({ queryKey: ["contacts-infinite"] });
      await queryClient.invalidateQueries({ queryKey: ["contact-groups"] });
      if (variables?.contactId) {
        await queryClient.invalidateQueries({
          queryKey: ["contact-detail", variables.contactId],
        });
      }
      showSuccess("Contact updated successfully");
    },
  });
};

export const useUpdateContactConsent = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: updateContactConsentAPI,
    onSuccess: async (data, variables) => {
      if (variables?.contactId) {
        queryClient.setQueryData(["contact-detail", variables.contactId], (current) => {
          if (!current?.contact) return current;
          return {
            ...current,
            contact: {
              ...current.contact,
              email_consent_status: data?.status || variables.status,
              email_consent_source: data?.source || variables.source || null,
              email_consent_at: data?.consentedAt ?? null,
              email_consent_by: data?.consentedBy ?? null,
              email_opt_out: (data?.status || variables.status) !== "consented",
            },
          };
        });
      }
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ["contacts"], refetchType: "all" }),
        queryClient.invalidateQueries({ queryKey: ["contacts-infinite"], refetchType: "all" }),
        queryClient.invalidateQueries({ queryKey: ["contact-groups"], refetchType: "all" }),
      ]);
      if (variables?.contactId) {
        await queryClient.invalidateQueries({
          queryKey: ["contact-detail", variables.contactId],
          refetchType: "active",
        });
      }
      showSuccess("Email consent updated");
    },
  });
};

export const useBulkUpdateContactConsent = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: bulkUpdateContactConsentAPI,
    onSuccess: async (data, variables) => {
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ["contacts"], refetchType: "all" }),
        queryClient.invalidateQueries({ queryKey: ["contacts-infinite"], refetchType: "all" }),
        queryClient.invalidateQueries({ queryKey: ["contact-groups"], refetchType: "all" }),
      ]);
      const requestedCount = variables?.contactIds?.length || 0;
      const updatedCount = data?.count || 0;
      if (updatedCount < requestedCount) {
        showWarning(
          `${updatedCount} of ${requestedCount} selected contacts updated; ${requestedCount - updatedCount} were no longer available.`,
        );
      } else {
        showSuccess(`${updatedCount} contact${updatedCount === 1 ? "" : "s"} updated`);
      }
    },
    onError: (error, variables) => {
      console.error("[Contacts][consent-failed]", {
        requestedCount: variables?.contactIds?.length || 0,
        error: error?.message || String(error),
      });
    },
  });
};

export const useContactDetail = (contactId, enabled = true) =>
  useQuery({
    queryKey: ["contact-detail", contactId],
    queryFn: () => fetchContactDetailAPI(contactId),
    enabled: enabled && Boolean(contactId),
    staleTime: 60 * 1000,
    retry: 2,
  });

export const useContactGroups = (
  groupBy,
  page = 1,
  limit = 10,
  searchQuery = "",
  appliedFilters = {},
  enabled = true,
) => {
  return useQuery({
    queryKey: [
      "contact-groups",
      groupBy,
      page,
      limit,
      searchQuery,
      appliedFilters,
    ],
    queryFn: () => fetchContactGroupsAPI(groupBy, page, limit, searchQuery, appliedFilters),
    enabled,
    staleTime: 5 * 60 * 1000,
    retry: 2,
    placeholderData: keepPreviousData,
  });
};
